import * as _ from 'lodash';
import { Break, ProviderSchedules, ProvidersState, SearchProvider } from './types';
import { ReduxAction, failure, pending, setAsyncData, success } from '../core';

import { ProvidersAction } from './actions';
import { ProviderData, RouteDetails } from '../../api/providers.types';

const initialState: ProvidersState = {
  searchText: '',
  isSearching: false,
  marketToProviderSchedules: {},
  providerLocationHistory: {},
  doctors: { isFetching: false },
  assistants: { isFetching: false },
  marketToBreaks: {},
  allProviders: { isFetching: false },
};

export function providersReducer(state: ProvidersState = initialState, action: ReduxAction<any>): ProvidersState {
  switch (action.type) {
    case ProvidersAction.PROVIDER_SEARCH_CHANGED:
      return {
        ...state,
        searchText: action.data as string,
      };

    case ProvidersAction.PROVIDER_SET_SEARCHING:
      return {
        ...state,
        isSearching: action.data as boolean,
      };

    case ProvidersAction.PROVIDER_SET_PROVIDERS:
      return {
        ...state,
        providers: action.data as SearchProvider[],
      };

    case ProvidersAction.SET_PROVIDER_SCHEDULES_FETCHING:
      return {
        ...state,
        marketToProviderSchedules: {
          ...state.marketToProviderSchedules,
          [action.data.marketCode]: setAsyncData(
            state.marketToProviderSchedules[action.data.marketCode] || { isFetching: false },
            (p) => (p.isFetching = action.data.value as boolean)
          ),
        },
        marketToBreaks: {
          ...state.marketToBreaks,
          [action.data.marketCode]: setAsyncData(state.marketToBreaks[action.data.marketCode] || { isFetching: false }, (p) => (p.isFetching = action.data.value as boolean)),
        },
      };

    case ProvidersAction.SET_PROVIDER_OFF_CALL:
      return {
        ...state,
        allProviders: {
          ...state.allProviders,
          data: state.allProviders.data?.slice().map((p) => {
            if (p.id === action.data.providerId) {
              return { ...p, availability: 'UNAVAILABLE' };
            }
            return p;
          }),
        },
        marketToProviderSchedules: _.mapValues(state.marketToProviderSchedules, (s) => {
          const data = s.data?.slice();
          if (data) {
            const index = data.findIndex((p) => p.provider.providerId === action.data.providerId);
            if (index > -1) {
              data[index].provider.availability = 'UNAVAILABLE';
            }
          }
          return { ...s, data };
        }),
      };

    case ProvidersAction.SET_PROVIDER_ON_CALL:
      return {
        ...state,
        allProviders: {
          ...state.allProviders,
          data: state.allProviders.data?.slice().map((p) => {
            if (p.id === action.data.providerId) {
              return { ...p, availability: 'ON_CALL' };
            }
            return p;
          }),
        },
        marketToProviderSchedules: _.mapValues(state.marketToProviderSchedules, (s) => {
          const data = s.data?.slice();
          if (data) {
            const index = data.findIndex((p) => p.provider.providerId === action.data.providerId);
            if (index > -1) {
              data[index].provider.availability = 'ON_CALL';
            }
          }
          return { ...s, data };
        }),
      };

    case ProvidersAction.SET_PROVIDER_SCHEDULES:
      const breaksList: Break[] = [];
      for (const provider of action.data.value as ProviderSchedules[]) {
        if (provider.breaks) {
          for (const singleBreak of provider.breaks) {
            singleBreak.marketCode = action.data.marketCode;
            singleBreak.scheduledTime = singleBreak.startTime;
            singleBreak.buildDoctorFullName =
              provider.provider.prefix + ' ' + provider.provider.firstName + ' ' + provider.provider.lastName + (provider.provider.suffix && ', ' + provider.provider.suffix);
            if (provider.provider.providerType === 'DOCTOR' || !singleBreak.doctorId) {
              breaksList.push(singleBreak);
            }
          }
        }
      }

      return {
        ...state,
        marketToProviderSchedules: {
          ...state.marketToProviderSchedules,
          [action.data.marketCode]: setAsyncData(
            state.marketToProviderSchedules[action.data.marketCode] || { isFetching: false },
            (p) => (p.data = action.data.value as ProviderSchedules[])
          ),
        },
        marketToBreaks: {
          ...state.marketToBreaks,
          [action.data.marketCode]: setAsyncData(state.marketToBreaks[action.data.marketCode] || { isFetching: false }, (p) => (p.data = breaksList as Break[])),
        },
      };

    case ProvidersAction.SET_PROVIDER_LOCATION_HISTORY_FETCHING:
      return {
        ...state,
        providerLocationHistory: {
          ...state.providerLocationHistory,
          [action.data.providerId]: setAsyncData(
            state.providerLocationHistory[action.data.providerId] || { isFetching: false },
            (p) => (p.isFetching = action.data.value as boolean)
          ),
        },
      };

    case ProvidersAction.SET_PROVIDER_LOCATION_HISTORY:
      return {
        ...state,
        providerLocationHistory: {
          ...state.providerLocationHistory,
          [action.data.providerId]: setAsyncData(
            state.providerLocationHistory[action.data.providerId] || { isFetching: false },
            (p) => (p.data = action.data.value as RouteDetails)
          ),
        },
      };

    case ProvidersAction.SET_IS_FETCHING_ALL_PROVIDERS:
      return {
        ...state,
        allProviders: setAsyncData(state.allProviders, (v) => (v.isFetching = action.data)),
      };

    case ProvidersAction.SET_ALL_PROVIDERS:
      return {
        ...state,
        allProviders: setAsyncData(state.allProviders, (v) => (v.data = action.data)),
      };
    case ProvidersAction.SET_ALL_PROVIDERS_ERROR:
      return {
        ...state,
        allProviders: setAsyncData(state.allProviders, (v) => (v.error = action.data)),
      };

    case pending(ProvidersAction.FETCH_DOCTORS):
      return {
        ...state,
        doctors: setAsyncData(state.doctors, (v) => (v.isFetching = true)),
      };

    case failure(ProvidersAction.FETCH_DOCTORS):
    case success(ProvidersAction.FETCH_DOCTORS):
    case ProvidersAction.SET_DOCTORS:
      return {
        ...state,
        doctors: setAsyncData(state.doctors, (v) => {
          v.data = _.sortBy(action.data.value as ProviderData[], ['firstName', 'lastName']);
          v.isFetching = false;
        }),
      };

    case pending(ProvidersAction.FETCH_ASSISTANTS):
      return {
        ...state,
        assistants: setAsyncData(state.assistants, (v) => (v.isFetching = true)),
      };

    case failure(ProvidersAction.FETCH_ASSISTANTS):
    case success(ProvidersAction.FETCH_ASSISTANTS):
    case ProvidersAction.SET_ASSISTANTS:
      return {
        ...state,
        assistants: setAsyncData(state.assistants, (v) => {
          v.data = _.sortBy(action.data.value as ProviderData[], ['firstName', 'lastName']);
          v.isFetching = false;
        }),
      };
    case pending(ProvidersAction.CREATE_BREAK):
      return {
        ...state,
      };
    case success(ProvidersAction.CREATE_BREAK):
    case ProvidersAction.CREATE_BREAK: {
      const marketToBreaks = { ...state.marketToBreaks };
      for (const market in state.marketToProviderSchedules) {
        if (market && state.marketToProviderSchedules[market].data) {
          const ps = state.marketToProviderSchedules[market].data!.find((p) => p.provider.providerId === action.data.value.doctorId);
          if (ps) {
            const newBreak = {
              ...action.data.value,
              marketCode: market,
              scheduledTime: action.data.value.startTime,
              buildDoctorFullName: ps.provider.prefix + ' ' + ps.provider.firstName + ' ' + ps.provider.lastName + (ps.provider.suffix && ', ' + ps.provider.suffix),
            };
            marketToBreaks[market] = setAsyncData(marketToBreaks[market], (p) => {
              const data = (p.data && p.data.slice()) || [];
              data.push(newBreak);
              p.data = data;
            });
          }
        }
      }

      return {
        ...state,
        marketToBreaks,
      };
    }

    case pending(ProvidersAction.EDIT_BREAK):
      return {
        ...state,
      };
    case success(ProvidersAction.EDIT_BREAK):
    case ProvidersAction.EDIT_BREAK: {
      const marketToBreaks = { ...state.marketToBreaks };
      const updateFirst = (br: Break, index: number) => {
        if (br.id === action.data.value.id) {
          const editedBreak = {
            ...br,
            scheduledTime: action.data.value.startTime,
            startTime: action.data.value.startTime,
            endTime: action.data.value.endTime,
            type: action.data.value.type,
            assistantId: action.data.value.assistantId,
          };
          marketToBreaks[br.marketCode] = setAsyncData(marketToBreaks[br.marketCode], (p) => {
            p.data = p.data!.slice();
            p.data[index] = editedBreak;
          });
          return true;
        }
        return false;
      };

      for (const market in marketToBreaks) {
        if (market && marketToBreaks[market].data) {
          marketToBreaks[market].data!.some(updateFirst);
        }
      }

      return {
        ...state,
        marketToBreaks,
      };
    }

    case pending(ProvidersAction.DELETE_BREAK):
      return {
        ...state,
      };
    case success(ProvidersAction.DELETE_BREAK):
    case ProvidersAction.DELETE_BREAK: {
      const marketToBreaks = { ...state.marketToBreaks };
      const updateFirst = (br: Break, index: number) => {
        if (br.id === action.data.value) {
          marketToBreaks[br.marketCode] = setAsyncData(marketToBreaks[br.marketCode], (p) => {
            p.data!.splice(index, 1);
          });
          return true;
        }
        return false;
      };

      for (const market in state.marketToBreaks) {
        if (market && marketToBreaks[market].data) {
          marketToBreaks[market].data!.some(updateFirst);
        }
      }
      return {
        ...state,
        marketToBreaks,
      };
    }

    default:
      return state;
  }
}
